import { useEffect,useRef,useState,useCallback } from "react";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { CoveredMatrixCallOption,CoveredMatrixGroup,CoveredMatrixMarketWatch,CoveredMatrixOrderPreviewResult,CreateCoveredMatrixOrderRequest,CoveredMatrixOrderTableReport,UpdateCoveredMatrixCriteriaRequest } from "src/Interfaces/coveredMatrixOrder";
import { getCoveredMatrixAccounts,getCoveredMatrixBaseInstruments,getCoveredMatrixCallOptions,createCoveredMatrixOrder,getCoveredMatrixOrder,getCoveredMatrixLiveCriteria,getCoveredMatrixMarketWatch,previewCoveredMatrixOrder,updateCoveredMatrixCriteria } from "src/Requests/coveredMatrixOrder";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { emptyDraft,ModalType,defaultCriterionType,isMaximumCostCriterion } from "./model";
import { validateDraft as getDraftError,validateEditedGroup } from "./validation";
import { toast } from "react-toastify";

export const useCoveredMatrixOrder=() => {
  const selectionRequest=useRef(0);
  const [optionDefaults,setOptionDefaults]=useState({ criterionType: defaultCriterionType,criterionValue: null as number|null });
  const [accounts,setAccounts]=useState<DropdownItem[]>([]);
  const [baseInstruments,setBaseInstruments]=useState<DropdownItem[]>([]);
  const [options,setOptions]=useState<CoveredMatrixCallOption[]>([]);
  const [draft,setDraft]=useState<CreateCoveredMatrixOrderRequest>(emptyDraft());
  const [modal,setModal]=useState<ModalType>(null);
  const [editingGroup,setEditingGroup]=useState<CoveredMatrixGroup|null>(null);
  const [editingOptions,setEditingOptions]=useState<CoveredMatrixCallOption[]>([]);
  const [editingMarketWatch,setEditingMarketWatch]=useState<Record<string,CoveredMatrixMarketWatch>>({});
  const [selectedGroup,setSelectedGroup]=useState<CoveredMatrixGroup|null>(null);
  const [tableVersion,setTableVersion]=useState(0);
  const [detailTableVersion,setDetailTableVersion]=useState(0);
  const [formLoadVersion,setFormLoadVersion]=useState(0);
  const [loading,setLoading]=useState(false);
  const [preview,setPreview]=useState<CoveredMatrixOrderPreviewResult|null>(null);
  const refresh=() => setTableVersion((version) => version+1);
  const changeDraft=(value: CreateCoveredMatrixOrderRequest) => {
    selectionRequest.current+=1;
    setPreview(null);
    setDraft(value);
    setSelectedGroup(null);
  };
  useEffect(() => {
    Promise.all([getCoveredMatrixAccounts(),getCoveredMatrixBaseInstruments()])
      .then(([nextAccounts,nextBases]) => {
        setAccounts(nextAccounts);
        setBaseInstruments(nextBases);
      })
      .catch(packetErrorHandler);
  },[]);
  useEffect(() => {
    let disposed=false;
    setOptions([]);
    if(draft.baseIsin)
      getCoveredMatrixCallOptions(draft.baseIsin)
        .then((result) => {
          if(!disposed)
            setOptions(result);
        })
        .catch((error) => {
          if(!disposed)
            packetErrorHandler(error);
        });
    return () => { disposed=true; };
  },[draft.baseIsin]);
  const pendingMaximumCostDefaultsKey=JSON.stringify(draft.items
    .filter((item) => isMaximumCostCriterion(item.criterionType)&&item.criterionValue===null)
    .map(({ optionIsin,criterionType }) => ({ optionIsin,criterionType })));
  useEffect(() => {
    const pendingItems: Array<{ optionIsin: string; criterionType: number }>=JSON.parse(pendingMaximumCostDefaultsKey);
    if(!draft.baseIsin||!pendingItems.length) return;
    let disposed=false;
    getCoveredMatrixLiveCriteria({
      baseIsin: draft.baseIsin,
      executionMethod: draft.executionMethod,
      manualBaseReferencePrice: draft.manualBaseReferencePrice,
      items: pendingItems,
    })
      .then((items) => {
        if(disposed)
          return;
        const defaults=new Map(items.map((item) => [item.optionIsin,item.value]));
        setDraft((current) => ({
          ...current,
          items: current.items.map((item) => {
            const value=defaults.get(item.optionIsin);
            return isMaximumCostCriterion(item.criterionType)&&
              item.criterionValue===null&&
              value!==null&&
              value!==undefined&&
              Number.isFinite(Number(value))
              ? { ...item,criterionValue: Number(value) }
              :item;
          }),
        }));
      })
      .catch(() => undefined);
    return () => { disposed=true; };
  },[draft.baseIsin,draft.executionMethod,draft.manualBaseReferencePrice,pendingMaximumCostDefaultsKey]);
  const accountName=(id: number) => accounts.find((x) => Number(x.id)===id)?.name||`اکانت ${id}`;
  const instrumentName=(id: string) => baseInstruments.find((x) => x.id.toString()===id)?.name||id;
  const optionName=(id: string) => options.find((x) => x.id.toString()===id)?.name||id;
  const openEdit=useCallback((groupId: number) => {
    if(!Number.isInteger(groupId)||groupId<=0)
      return;
    setLoading(true);
    setEditingOptions([]);
    setEditingMarketWatch({});
    getCoveredMatrixOrder(groupId)
      .then(async (details) => {
        const editableGroup={ ...details,items: (details.items||[]).map((item) => ({ ...item })),removedItems: [],applyToSimilar: false };
        const [nextOptions,nextMarketWatch]=await Promise.all([
          getCoveredMatrixCallOptions(details.baseIsin),
          getCoveredMatrixMarketWatch([details.baseIsin,...editableGroup.items.map((item) => item.optionIsin)])
            .catch(() => ({} as Record<string,CoveredMatrixMarketWatch>)),
        ]);
        setEditingGroup(editableGroup);
        setEditingOptions(nextOptions);
        setEditingMarketWatch(nextMarketWatch);
        setModal("edit");
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  },[]);
  const openDetails=useCallback((row: CoveredMatrixOrderTableReport) => {
    if(!Number.isInteger(row.id)||row.id<=0)
      return;
    selectionRequest.current+=1;
    const items=(row.optionIsins||[]).map((optionIsin,index) => ({
      id: index,
      optionIsin,
      optionName: row.optionSymbolNames?.[index]||optionIsin,
      criterionType: row.criterionTypes?.[index]??defaultCriterionType,
      criterionValue: row.criterionValues?.[index]??null,
      coveredCallOrderId: row.coveredCallOrderIds?.[index]??null,
    }));
    setSelectedGroup({
      id: row.id,
      batchId: "",
      sahraUserId: row.sahraUserId,
      baseIsin: row.baseIsin,
      baseSymbolName: row.baseSymbolName,
      executionMethod: row.executionMethod,
      manualBaseReferencePrice: row.manualBaseReferencePrice,
      volumeCalculationType: row.volumeCalculationType,
      volumeInput: row.volumeInput,
      targetVolume: row.targetVolume,
      sentVolume: row.sentVolume,
      baseHoldingVolume: row.baseHoldingVolume,
      imbalance: row.imbalance,
      sendOrderType: row.sendOrderTypeValue,
      state: row.state,
      items,
    });
    setDetailTableVersion((version) => version+1);
    setDraft({
      accountIds: [row.sahraUserId],
      baseIsin: row.baseIsin,
      executionMethod: row.executionMethod,
      manualBaseReferencePrice: row.manualBaseReferencePrice,
      volumeCalculationType: row.volumeCalculationType,
      volumeInput: row.volumeInput,
      sendOrderType: row.sendOrderTypeValue,
      items: items.map((item) => ({
        optionIsin: item.optionIsin,
        criterionType: item.criterionType,
        criterionValue: item.criterionValue,
      })),
    });
    setFormLoadVersion((version) => version+1);
  },[]);
  const reloadSelectedDetails=useCallback((groupId: number) => {
    if(selectedGroup?.id!==groupId)
      return;
    getCoveredMatrixOrder(groupId)
      .then((details) => {
        setSelectedGroup((current) => current?.id===groupId? details:current);
        setDetailTableVersion((version) => version+1);
      })
      .catch(packetErrorHandler);
  },[selectedGroup?.id]);
  const openTradeDetails=useCallback((row: CoveredMatrixOrderTableReport) => {
    if(!Number.isInteger(row.id)||row.id<=0)
      return;
    const request=++selectionRequest.current;
    getCoveredMatrixOrder(row.id)
      .then((details) => {
        if(request!==selectionRequest.current)
          return;
        setSelectedGroup({ ...details,imbalance: row.imbalance });
        setDetailTableVersion((version) => version+1);
      })
      .catch(packetErrorHandler);
  },[]);
  const handleGroupsDeleted=useCallback((groupIds: number[]) => {
    setSelectedGroup((current) => current&&groupIds.includes(current.id)? null:current);
  },[]);
  const validateDraft=() => {
    const error=getDraftError(draft);
    if(error)
      toast.error(error);
    return !error;
  };
  const openPreview=() => {
    if(loading||!validateDraft())
      return;
    setLoading(true);
    setPreview(null);
    previewCoveredMatrixOrder(draft)
      .then((response) => {
        setPreview(response);
        setModal("preview");
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  const create=() => {
    if(loading||!validateDraft()||!preview?.rows.length)
      return;
    setLoading(true);
    createCoveredMatrixOrder(draft)
      .then((response) => {
        response.errors.forEach((error) => toast.error(error));
        response.warnings?.forEach((warning) => toast.warning(warning));
        if(response.groups.length)
          toast.success(`${response.groups.length} گروه ثبت شد.`);
        setModal(null);
        setPreview(null);
        refresh();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  const openConnections=() => {
    if(!preview?.rows.length)
      return;
    setModal("connections");
  };
  const updateCriteria=(editedGroup: CoveredMatrixGroup) => {
    if(loading)
      return;
    const error=validateEditedGroup(editedGroup);
    if(error) {
      toast.error(error);
      return;
    }
    const request: UpdateCoveredMatrixCriteriaRequest={
      groupId: editedGroup.id,
      targetVolume: editedGroup.targetVolume,
      applyToSimilar: Boolean(editedGroup.applyToSimilar),
      items: [...editedGroup.items.map((item) => ({
        id: item.id>0? item.id:0,
        optionIsin: item.optionIsin,
        criterionType: item.criterionType,
        criterionValue: item.criterionValue,
        isDeleted: false,
      })),...(editedGroup.removedItems||[]).filter((item) => item.id>0).map((item) => ({
        id: item.id,
        optionIsin: item.optionIsin,
        criterionType: item.criterionType,
        criterionValue: item.criterionValue,
        isDeleted: true,
      }))],
    };
    setLoading(true);
    updateCoveredMatrixCriteria(request)
      .then(() => {
        toast.success("مقادیر معیار به‌روزرسانی شد.");
        setModal(null);
        reloadSelectedDetails(editedGroup.id);
        refresh();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  return {
    optionDefaults,
    setOptionDefaults,
    accounts,
    baseInstruments,
    options,
    draft,
    modal,
    setModal,
    editingGroup,
    editingOptions,
    editingMarketWatch,
    selectedGroup,
    tableVersion,
    detailTableVersion,
    formLoadVersion,
    loading,
    preview,
    changeDraft,
    accountName,
    instrumentName,
    optionName,
    openEdit,
    openDetails,
    reloadSelectedDetails,
    openTradeDetails,
    handleGroupsDeleted,
    openPreview,
    create,
    openConnections,
    updateCriteria,
  };
};
